export const DEFAULT_MICROPHONE_ID = 'default';
export const DEFAULT_SPEAKER_ID = 'default';
export const DEFAULT_CAMERA_ID = 'default';
export const COMMUNICATIONS_DEVICE_ID = 'communications';

export const normalizeText = (value) => String(value || '').trim().toLowerCase();

export const normalizeDeviceLabel = (value) =>
  normalizeText(value)
    .replace(/^(default|communications)\s*-\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const isAliasDevice = (deviceId) =>
  [DEFAULT_MICROPHONE_ID, DEFAULT_SPEAKER_ID, COMMUNICATIONS_DEVICE_ID].includes(deviceId);

export const createSelectionId = (device = {}, fallbackId) =>
  device.selectionId ||
  (!isAliasDevice(device.deviceId) && device.deviceId) ||
  (device.groupId && `${device.groupId}::${normalizeDeviceLabel(device.displayLabel || device.label)}`) ||
  normalizeDeviceLabel(device.displayLabel || device.label) ||
  fallbackId;

export const buildStoredPreference = (device = {}, fallbackId, fallbackLabel) => ({
  selectionId: createSelectionId(device, fallbackId),
  deviceId: device.deviceId || fallbackId,
  groupId: device.groupId || '',
  label: device.displayLabel || device.label || fallbackLabel,
  canonicalLabel: normalizeDeviceLabel(device.displayLabel || device.label || fallbackLabel),
});

export const createStoredPreferenceHelpers = ({ storageKey, fallbackId, fallbackLabel }) => {
  const defaultPreference = {
    selectionId: fallbackId,
    deviceId: fallbackId,
    groupId: fallbackId,
    label: fallbackLabel,
  };

  const readPreference = () => {
    if (typeof window === 'undefined') {
      return defaultPreference;
    }

    try {
      const rawValue = window.sessionStorage.getItem(storageKey);
      if (!rawValue) return defaultPreference;

      const parsedValue = JSON.parse(rawValue);
      if (parsedValue && typeof parsedValue === 'object') {
        return {
          selectionId: parsedValue.selectionId || parsedValue.deviceId || fallbackId,
          deviceId: parsedValue.deviceId || fallbackId,
          groupId: parsedValue.groupId || '',
          label: parsedValue.label || fallbackLabel,
          canonicalLabel: parsedValue.canonicalLabel || normalizeDeviceLabel(parsedValue.label || fallbackLabel),
        };
      }
    } catch (error) {}

    return defaultPreference;
  };

  const persistPreference = (preference) => {
    if (typeof window === 'undefined') return;

    try {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          selectionId: preference?.selectionId || fallbackId,
          deviceId: preference?.deviceId || fallbackId,
          groupId: preference?.groupId || '',
          label: preference?.label || fallbackLabel,
          canonicalLabel: preference?.canonicalLabel || normalizeDeviceLabel(preference?.label || fallbackLabel),
        })
      );
    } catch (error) {}
  };

  return {
    defaultPreference,
    readPreference,
    persistPreference,
  };
};

export const normalizeDeviceOptions = ({
  devices,
  kind,
  fallbackId,
  fallbackLabel,
}) => {
  const matchingDevices = devices.filter((device) => device.kind === kind);
  const realDevices = matchingDevices.filter((device) => !isAliasDevice(device.deviceId));
  const uniqueDevices = [];
  const seenKeys = new Set();

  realDevices.forEach((device) => {
    const uniqueKey = [
      normalizeDeviceLabel(device.label),
      device.groupId || 'missing-group-id',
      kind,
    ].join('::');

    if (!seenKeys.has(uniqueKey)) {
      seenKeys.add(uniqueKey);
      uniqueDevices.push(device);
    }
  });

  const fallbackDevice = {
    deviceId: fallbackId,
    groupId: fallbackId,
    kind,
    label: fallbackLabel,
  };

  const baseDevices = [fallbackDevice, ...uniqueDevices];

  return baseDevices
    .slice()
    .sort((left, right) => {
      if (left.deviceId === fallbackId) return -1;
      if (right.deviceId === fallbackId) return 1;
      return String(left.label || '').localeCompare(String(right.label || ''));
    })
    .map((device, index) => {
      const displayLabel =
        device.deviceId === fallbackId
          ? fallbackLabel
          : device.label?.trim() || `${fallbackLabel} ${index}`;

      const selectionId = createSelectionId({
        deviceId: device.deviceId,
        groupId: device.groupId,
        label: displayLabel,
      }, fallbackId);

      return {
        ...device,
        selectionId,
        displayLabel,
        optionKey: `${selectionId}-${device.groupId || 'missing-group'}-${index}`,
      };
    });
};

export const resolveMatchingDevice = (options, preference, fallbackId) => {
  if (!Array.isArray(options) || options.length === 0) return null;
  if (!preference) {
    return options.find((option) => option.deviceId === fallbackId) || options[0] || null;
  }

  return (
    options.find((option) => option.selectionId === preference.selectionId) ||
    options.find(
      (option) =>
        preference.groupId &&
        normalizeText(option.groupId) === normalizeText(preference.groupId) &&
        normalizeDeviceLabel(option.displayLabel) === normalizeText(preference.canonicalLabel || preference.label)
    ) ||
    options.find(
      (option) =>
        preference.deviceId &&
        option.deviceId === preference.deviceId
    ) ||
    options.find(
      (option) =>
        preference.canonicalLabel &&
        normalizeDeviceLabel(option.displayLabel) === normalizeText(preference.canonicalLabel)
    ) ||
    options.find(
      (option) =>
        preference.groupId &&
        normalizeText(option.groupId) === normalizeText(preference.groupId)
    ) ||
    options.find(
      (option) =>
        preference.label &&
        normalizeDeviceLabel(option.displayLabel) === normalizeDeviceLabel(preference.label)
    ) ||
    options.find((option) => option.deviceId === fallbackId) ||
    options[0] ||
    null
  );
};
